import React, { useContext, useEffect, useState, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from "react-router-dom";
import {
    Button,
    Card,
    DatePicker,
    FloatButton,
    Progress,
    Space,
    Spin,
    Table,
    TableProps,
    Tag,
    Tooltip,
    Typography,
    Dropdown,
    Input,
    Select,
    Modal,
    message
} from "antd";
import { MenuOutlined, ArrowUpOutlined, ArrowDownOutlined, MoreOutlined, CaretDownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColumnsType } from "antd/es/table";
import { BiArrowFromLeft, BiRefresh, BiPlus, BiCopy } from "react-icons/bi";
import { FaEye, FaFilePdf, FaRegFilePdf, FaUsers, FaLaptopCode } from "react-icons/fa";
import { FaListCheck } from "react-icons/fa6";
import { VscSettingsGear } from "react-icons/vsc";
import { IoOptions } from "react-icons/io5";
import { AuthContext } from "../../../../config/AuthContext";
import { Content } from "antd/es/layout/layout";
import AppSider from "../../components/AppSider";
import UseWebSocketSubscription from "../../../UseWebSocketSubscription";
import { GetBbddActivitats } from "../../../../dao/bbdd/GetBbddActivitats";
import { ReorderBbddActivitats } from "../../../../dao/bbdd/ReorderBbddActivitats";
import { PostPractica } from "../../../../dao/bbdd/PostPractica";
import { GetUsuariIdActivitatsIdPdf } from "../../../../dao/GetUsuariIdActivitatsIdPdf";
import { GetUsuariIdActivitatsIdPdfSol } from "../../../../dao/GetUsuariIdActivitatsIdPdfSol";
import ConfigActivitatModal from "../ConfigActivitatModal";
import CopiarActivitatModal from "./CopiarActivitatModal";
import ExercicisModal from "./ExercicisModal";
import ActivitatAlumnesBbddModal from "./ActivitatAlumnesBbddModal";
import BbddColors from "../../../../styles/BbddColors";
import AvaluacioColors from "../../../../styles/AvaluacioColors";
import { aulaColor as aulaColorFn } from "../../../../styles/JutgeColors";

const { Text } = Typography;

interface RowContextProps {
    setActivatorNodeRef?: (element: HTMLElement | null) => void;
    listeners?: any;
    attributes?: any;
}

const RowContext = React.createContext<RowContextProps>({});

interface DragRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    'data-row-key': string;
}

const TableDragRow = (props: DragRowProps) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: props['data-row-key'],
    });

    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };

    const contextValue = useMemo(
        () => ({ setActivatorNodeRef, listeners, attributes }),
        [setActivatorNodeRef, listeners, attributes]
    );

    return (
        <RowContext.Provider value={contextValue}>
            <tr {...props} ref={setNodeRef} style={style} />
        </RowContext.Provider>
    );
};

const DragHandle: React.FC = () => {
    const { setActivatorNodeRef, listeners, attributes } = useContext(RowContext);
    return (
        <span
            ref={setActivatorNodeRef}
            {...listeners}
            {...attributes}
            style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}
        >
            <MenuOutlined style={{ color: '#999', pointerEvents: 'none' }} />
        </span>
    );
};

interface ActivitatsProps {
    onlyExams?: boolean;
}

const Activitats: React.FC<ActivitatsProps> = ({ onlyExams = false }) => {
    const { isLoggedIn, userId, user, setBreadCrumb } = useContext(AuthContext);
    const navigate = useNavigate();
    const [dades, setDades] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [modalExercicis, setModalExercicis] = useState(false);
    const [modalAlumnes, setModalAlumnes] = useState(false);
    const [selected, setSelected] = useState<any>();
    const [pantalla, setPantalla] = useState(onlyExams ? 3 : 2);
    const [copyModal, setCopyModal] = useState(false);
    const [selectedAula, setSelectedAula] = useState<string>('all');
    const [searchText, setSearchText] = useState<string>('');

    const isTeacher = user.tipus === "0" || user.tipus === "1";
    const [startingExamId, setStartingExamId] = useState<string | number | null>(null);
    const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);
    const [updatingDateId, setUpdatingDateId] = useState<number | null>(null);

    async function handleQuickChangeEstat(record: any, newEstat: number) {
        if (updatingStatusId) return;
        setUpdatingStatusId(record.id);
        try {
            const payload = {
                id: record.id,
                nom: record.nom,
                estat: newEstat,
                id_aula: record.id_aula,
                idAula: record.id_aula,
                is_examen: record.is_examen ? 1 : 0,
                isExamen: record.is_examen ? 1 : 0,
                context: record.context || "",
                tipus_aula: record.tipus_aula ?? record.tipus ?? 0,
                inici: record.inici ? dayjs(record.inici.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null,
                final: record.final ? dayjs(record.final.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null,
                ras: (record.ras || []).map((r: any) => ({
                    id: r.id,
                    percentatge: r.percentatge || 0,
                    instrument: r.instrument || ""
                }))
            };
            await PostPractica(payload);
            message.success("Estat de l'activitat actualitzat");
            await checkDades();
        } catch (err: any) {
            console.error("Error al canviar l'estat:", err);
            message.error("Error al canviar l'estat");
        } finally {
            setUpdatingStatusId(null);
        }
    }

    async function handleQuickChangeDate(record: any, field: 'inici' | 'final', newDate: dayjs.Dayjs | null) {
        if (updatingDateId) return;
        setUpdatingDateId(record.id);
        try {
            const formattedDate = newDate ? newDate.format('YYYY-MM-DD HH:mm:ss') : null;
            const payload = {
                id: record.id,
                nom: record.nom,
                estat: record.estat,
                id_aula: record.id_aula,
                idAula: record.id_aula,
                is_examen: record.is_examen ? 1 : 0,
                isExamen: record.is_examen ? 1 : 0,
                context: record.context || "",
                tipus_aula: record.tipus_aula ?? record.tipus ?? 0,
                inici: field === 'inici' ? formattedDate : (record.inici ? dayjs(record.inici.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null),
                final: field === 'final' ? formattedDate : (record.final ? dayjs(record.final.toString().substring(0, 19).replace('T', ' ')).format('YYYY-MM-DD HH:mm:ss') : null),
                ras: (record.ras || []).map((r: any) => ({
                    id: r.id,
                    percentatge: r.percentatge || 0,
                    instrument: r.instrument || ""
                }))
            };
            await PostPractica(payload);
            message.success(`Data de ${field === 'inici' ? 'inici' : 'finalització'} actualitzada`);
            await checkDades();
        } catch (err: any) {
            console.error("Error al actualitzar la data:", err);
            message.error("Error al actualitzar la data");
        } finally {
            setUpdatingDateId(null);
        }
    }

    UseWebSocketSubscription(
        ["/topic/home", ...(userId ? [`/topic/exam/reset/${userId}`] : [])],
        (topic: string, data: any) => {
            if (topic === "/topic/home" || (data && data.reset)) {
                checkDades();
            }
        }
    );

    const handleStartExam = async (ele: any) => {
        const isExam = Number(ele.is_examen ?? ele.isExamen) === 1;
        if (!isExam) {
            navigate(`/database/activitats/${ele.id}`);
            return;
        }

        Modal.confirm({
            title: `Iniciar l'examen: ${ele.nom}?`,
            content: "Aquest botó és d'un sol ús. Un cop entris, l'examen quedarà registrat com a iniciat. Si surts i necessites tornar a entrar, l'haurà de reiniciar el professor.",
            okText: "Iniciar exàmen",
            okButtonProps: { type: 'primary', danger: true },
            cancelText: "Cancel·lar",
            onOk: async () => {
                setStartingExamId(ele.id);
                try {
                    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost') + ':8061';
                    const token = localStorage.getItem('authToken');
                    if (user.tipus === "2") {
                        await fetch(`${baseUrl}/jutge/exam/${ele.id}/start`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify({ id_usuari: userId })
                        }).catch(() => { /* ignore */ });
                    }
                    navigate(`/database/activitats/${ele.id}`);
                } catch (e) {
                    message.error("Error en iniciar l'examen");
                } finally {
                    setStartingExamId(null);
                }
            }
        });
    };

    useEffect(() => {
        if (isLoggedIn) {
            setBreadCrumb([
                { nom: 'Home', nav: '/home' },
                { nom: 'database', nav: '/database' },
                ...(onlyExams
                    ? [{ nom: 'exàmens', nav: '/database/examens' }]
                    : [{ nom: 'activitats', nav: '/database/activitats' }])
            ], true);
            checkDades();
        }
    }, [onlyExams]);

    async function checkDades() {
        setLoading(true);
        try {
            const data = await GetBbddActivitats(userId);
            let parsedList: any[] = [];
            if (Array.isArray(data)) {
                parsedList = data;
            } else if (typeof data === 'string') {
                try {
                    const parsed = JSON.parse(data);
                    parsedList = Array.isArray(parsed) ? parsed : [];
                } catch {
                    parsedList = [];
                }
            } else if (data && (data as any).json) {
                try {
                    const parsed = typeof (data as any).json === 'string' ? JSON.parse((data as any).json) : (data as any).json;
                    parsedList = Array.isArray(parsed) ? parsed : [];
                } catch {
                    parsedList = [];
                }
            }
            setDades(parsedList);
        } catch (e) {
            console.error("Error al carregar activitats de BBDD:", e);
            setDades([]);
        } finally {
            setLoading(false);
        }
    }

    function showSettings(ele: any) {
        setSelected(ele);
        setModal(true);
    }

    function showExercicis(ele: any) {
        setSelected(ele);
        setModalExercicis(true);
    }

    async function getPdf(ele: any) {
        setLoading(true);
        const documentUrl = await GetUsuariIdActivitatsIdPdf(ele.id, userId);
        if (documentUrl && documentUrl !== "") {
            const link = document.createElement('a');
            link.href = documentUrl;
            link.download = `activitat${ele.id}.pdf`;
            link.target = '_blank';
            link.click();
        }
        setLoading(false);
    }

    async function getPdfSol(ele: any) {
        setLoading(true);
        const documentUrl = await GetUsuariIdActivitatsIdPdfSol(ele.id, userId);
        if (documentUrl && documentUrl !== "") {
            const link = document.createElement('a');
            link.href = documentUrl;
            link.download = `activitatSol${ele.id}.pdf`;
            link.target = '_blank';
            link.click();
        }
        setLoading(false);
    }

    function onCloseModal(reload: boolean) {
        setModal(false);
        setModalExercicis(false);
        if (reload) checkDades();
        else setLoading(false);
    }

    const safeDades = Array.isArray(dades) ? dades : [];

    // Filter data by onlyExams, aula and search text
    const filteredDades = safeDades.filter((item: any) => {
        const isExam = item.is_examen === 1 || item.is_examen === true;
        if (onlyExams && !isExam) return false;
        if (!onlyExams && isExam) return false;

        if (selectedAula !== 'all' && String(item.id_aula) !== String(selectedAula)) {
            return false;
        }

        if (searchText.trim() !== '') {
            const search = searchText.toLowerCase();
            const nom = (item.nom || '').toLowerCase();
            const aula = String(item.id_aula || '').toLowerCase();
            return nom.includes(search) || aula.includes(search);
        }

        return true;
    });

    // Extract unique classrooms
    const availableAulas = Array.from(
        new Map(safeDades.map((d: any) => [String(d.id_aula), d.aula || `Aula ${d.id_aula}`])).entries()
    ).map(([id, nom]) => ({ id, nom }));


    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        })
    );

    const onDragEnd = async ({ active, over }: DragEndEvent) => {
        if (active.id !== over?.id) {
            setDades((previous) => {
                const activeIndex = previous.findIndex((i) => i.id === active.id);
                const overIndex = previous.findIndex((i) => i.id === over?.id);
                if (activeIndex === -1 || overIndex === -1) return previous;
                const newDades = arrayMove(previous, activeIndex, overIndex);

                const ids = newDades.map(d => d.id + "");
                ReorderBbddActivitats(ids).catch(() => {
                    message.error("Error al guardar l'ordre");
                    checkDades();
                });

                return newDades;
            });
        }
    };

    const moveUp = (index: number) => {
        if (index > 0) {
            const currentItem = filteredDades[index];
            const prevItem = filteredDades[index - 1];
            const activeIndex = dades.findIndex((i) => i.id === currentItem.id);
            const overIndex = dades.findIndex((i) => i.id === prevItem.id);
            if (activeIndex !== -1 && overIndex !== -1) {
                const newDades = arrayMove(dades, activeIndex, overIndex);
                setDades(newDades);
                const ids = newDades.map((d: any) => d.id + "");
                ReorderBbddActivitats(ids);
            }
        }
    };

    const moveDown = (index: number) => {
        if (index < filteredDades.length - 1) {
            const currentItem = filteredDades[index];
            const nextItem = filteredDades[index + 1];
            const activeIndex = dades.findIndex((i) => i.id === currentItem.id);
            const overIndex = dades.findIndex((i) => i.id === nextItem.id);
            if (activeIndex !== -1 && overIndex !== -1) {
                const newDades = arrayMove(dades, activeIndex, overIndex);
                setDades(newDades);
                const ids = newDades.map((d: any) => d.id + "");
                ReorderBbddActivitats(ids);
            }
        }
    };

    const columns: ColumnsType<any> = [
        {
            key: 'ord',
            width: 50,
            align: 'center',
            hidden: !isTeacher,
            render: () => <DragHandle />,
        },
        {
            key: 'sort',
            title: 'Ordre',
            width: 80,
            align: 'center',
            hidden: !isTeacher,
            render: (_text, _ele, index) => (
                <Space direction="vertical" size={0}>
                    <Button
                        type="text"
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={index === 0}
                        onClick={() => moveUp(index)}
                    />
                    <Button
                        type="text"
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={index === filteredDades.length - 1}
                        onClick={() => moveDown(index)}
                    />
                </Space>
            )
        },
        {
            key: 'avatar',
            dataIndex: 'avatar',
            width: 50,
            render: (imgString: string) => {
                if (!imgString) return null;
                const base64 = imgString.split(':').pop();
                return (
                    <img
                        src={`data:image/png;base64,${base64}`}
                        alt="avatar"
                        style={{
                            width: 44,
                            height: 44,
                            objectFit: 'cover',
                            borderRadius: '10px',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                        }}
                    />
                );
            }
        },
        {
            key: 'nom',
            dataIndex: 'nom',
            width: 200,
            title: onlyExams ? 'Examen' : 'Activitat',
            render: (text, ele) => {
                const aulaClr = aulaColorFn(ele.id_aula);
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <Text strong style={{ fontSize: '1rem' }}>{text}</Text>
                            {ele.is_examen === 1 && (
                                <Tag color="volcano" style={{ fontWeight: 600 }}>Examen</Tag>
                            )}
                            {ele.escena && (
                                <Tag color="geekblue" style={{ fontSize: '0.75rem' }}>🎮 {ele.escena}</Tag>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {ele.id !== undefined && ele.id !== null && (
                                <Text style={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.3)', marginTop: 1 }}>
                                    ID: {ele.id}
                                </Text>
                            )}
                            {ele.id_aula && (
                                <Tag color={aulaClr} style={{ color: '#fff', border: 'none', fontWeight: 600 }}>
                                    {ele.aula || `Aula ${ele.id_aula}`}
                                </Tag>
                            )}
                        </div>

                        {ele.context && (
                            <Text type="secondary" style={{ fontSize: '0.8rem', maxWidth: 380, marginTop: 2 }} ellipsis>
                                {ele.context}
                            </Text>
                        )}
                        {((ele.ras && ele.ras.length > 0) || ele.tags) && (
                            <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {(ele.ras || []).map((ra: any) => {
                                    const raColors = [
                                        'blue', 'geekblue', 'purple', 'magenta', 'red', 'orange', 'gold', 'lime', 'green', 'cyan'
                                    ];
                                    const nomStr = (ra.nom !== undefined && ra.nom !== null) ? String(ra.nom) : '';
                                    const raNum = parseInt(nomStr.replace(/\D/g, ''), 10) || ra.id || 1;
                                    const color = raColors[Math.abs(raNum - 1) % raColors.length];
                                    const displayNom = nomStr.toUpperCase().startsWith('RA') ? nomStr : (nomStr ? `RA${nomStr}` : `RA${ra.id}`);

                                    return (
                                        <Tag key={`ra_${ra.id || Math.random()}`} color={color} style={{ fontSize: '0.75rem', margin: 0 }}>
                                            {displayNom}{ra.instrument ? ` - ${ra.instrument}` : ''}
                                        </Tag>
                                    );
                                })}
                                {ele.tags && (
                                    (Array.isArray(ele.tags) ? ele.tags : String(ele.tags).split(/[\s,]+/))
                                        .map((t: string) => t.trim())
                                        .filter(Boolean)
                                        .map((tagStr: string, idx: number) => (
                                            <Tag
                                                key={`tag-${idx}`}
                                                style={{
                                                    background: '#f0f5ff',
                                                    border: '1px solid #adc6ff',
                                                    color: '#1d39c4',
                                                    borderRadius: 12,
                                                    padding: '0 8px',
                                                    fontSize: '0.72rem',
                                                    fontWeight: 500,
                                                    margin: 0
                                                }}
                                            >
                                                #{tagStr}
                                            </Tag>
                                        ))
                                )}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'dates',
            title: 'Dates',
            width: 175,
            align: 'center',
            hidden: !isTeacher,
            render: (_, ele: any) => {
                const iniciVal = ele.inici ? dayjs(ele.inici.toString().substring(0, 19)) : null;
                const finalVal = ele.final ? dayjs(ele.final.toString().substring(0, 19)) : null;
                const formattedInici = iniciVal ? iniciVal.format('DD/MM/YYYY HH:mm') : null;
                const formattedFinal = finalVal ? finalVal.format('DD/MM/YYYY HH:mm') : null;

                if (!isTeacher) {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                            {formattedInici ? <Tag color="green" style={{ margin: 0 }}>{formattedInici}</Tag> : <span style={{ color: '#bbb' }}>-</span>}
                            {formattedFinal ? <Tag color="red" style={{ margin: 0 }}>{formattedFinal}</Tag> : <span style={{ color: '#bbb' }}>-</span>}
                        </div>
                    );
                }

                const isUpdating = updatingDateId === ele.id;
                return (
                    <div
                        style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Tooltip title="Fes clic per modificar la data d'inici">
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: ele.inici ? '#f6ffed' : 'transparent',
                                borderRadius: 4,
                                padding: '0 6px',
                                height: 22
                            }}>
                                <DatePicker
                                    size="small"
                                    showTime={{ format: 'HH:mm' }}
                                    format="DD/MM/YYYY HH:mm"
                                    disabled={isUpdating}
                                    value={iniciVal}
                                    onChange={(d) => handleQuickChangeDate(ele, 'inici', d)}
                                    allowClear
                                    placeholder="Sense inici"
                                    style={{
                                        padding: 0,
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        width: formattedInici ? 'auto' : 85,
                                        fontSize: '0.75rem',
                                        color: ele.inici ? '#389e0d' : undefined
                                    }}
                                />
                            </div>
                        </Tooltip>

                        <Tooltip title="Fes clic per modificar la data final">
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: ele.final ? '#fff1f0' : 'transparent',
                                borderRadius: 4,
                                padding: '0 6px',
                                height: 22
                            }}>
                                <DatePicker
                                    size="small"
                                    showTime={{ format: 'HH:mm' }}
                                    format="DD/MM/YYYY HH:mm"
                                    disabled={isUpdating}
                                    value={finalVal}
                                    onChange={(d) => handleQuickChangeDate(ele, 'final', d)}
                                    allowClear
                                    placeholder="Sense final"
                                    style={{
                                        padding: 0,
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        width: formattedFinal ? 'auto' : 85,
                                        fontSize: '0.75rem',
                                        color: ele.final ? '#cf1322' : undefined
                                    }}
                                />
                            </div>
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            key: 'estat',
            dataIndex: 'estat',
            title: 'Estat',
            align: 'center',
            width: 110,
            render: (e, ele) => {
                const isCurrentUpdating = updatingStatusId === ele.id;

                const tagElement = (
                    <Tag
                        color={
                            e === 3 ? BbddColors.colors.color5 :
                                e === 2 ? BbddColors.colors.color3 :
                                    e === 4 ? BbddColors.colors.color4 :
                                        BbddColors.colors.color1
                        }
                        style={{
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            margin: 0,
                            cursor: isTeacher ? 'pointer' : 'default',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            userSelect: 'none',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {isCurrentUpdating ? <Spin size="small" style={{ marginRight: 4 }} /> : null}
                        {e === 3 ? 'Actiu' : e === 2 ? 'Tancat' : e === 4 ? 'Revisió' : 'No visible'}
                        {isTeacher && !isCurrentUpdating && (
                            <CaretDownOutlined style={{ fontSize: '0.65rem', opacity: 0.7 }} />
                        )}
                    </Tag>
                );

                const statusMenu = isTeacher ? {
                    items: [
                        {
                            key: '3',
                            label: <Tag color={BbddColors.colors.color5} style={{ fontWeight: 'bold' }}>Actiu</Tag>,
                            onClick: () => handleQuickChangeEstat(ele, 3)
                        },
                        {
                            key: '2',
                            label: <Tag color={BbddColors.colors.color3} style={{ fontWeight: 'bold' }}>Tancat</Tag>,
                            onClick: () => handleQuickChangeEstat(ele, 2)
                        },
                        {
                            key: '1',
                            label: <Tag color={BbddColors.colors.color1} style={{ fontWeight: 'bold' }}>No visible</Tag>,
                            onClick: () => handleQuickChangeEstat(ele, 1)
                        },
                        {
                            key: '4',
                            label: <Tag color={BbddColors.colors.color4} style={{ fontWeight: 'bold' }}>Revisió</Tag>,
                            onClick: () => handleQuickChangeEstat(ele, 4)
                        }
                    ]
                } : undefined;

                if (isTeacher && statusMenu) {
                    return (
                        <Dropdown
                            menu={statusMenu}
                            trigger={['click']}
                            disabled={Boolean(updatingStatusId)}
                        >
                            <Tooltip title="Canviar estat">
                                <span style={{ cursor: 'pointer' }}>
                                    {tagElement}
                                </span>
                            </Tooltip>
                        </Dropdown>
                    );
                }
                return tagElement;
            }
        },
        {
            key: 'progres',
            dataIndex: 'progres',
            title: 'Progrés',
            width: 170,
            render: (e, ele) => (
                // Per a exàmens, l'alumne només veu el progrés/nota si l'activitat està TANCAT (2)
                // o en REVISIÓ (4). En actiu (3) encara no es mostra. El professor sempre el veu.
                (!ele.is_examen || ele.estat === 2 || ele.estat === 4 || isTeacher) && (
                    <Progress
                        type="circle"
                        percent={ele.progres || 0}
                        strokeColor={{
                            '0%': BbddColors.colors.color6,
                            '100%': BbddColors.colors.color1,
                        }}
                        status={ele.progres === 100 ? "success" : "active"}
                        size="small"
                        format={(percent) => <span style={{ fontWeight: 'bold' }}>{percent}%</span>}
                    />
                )
            )
        },
        {
            key: 'actions',
            title: 'Accions',
            align: 'center',
            width: 150,
            render: (e, ele) => (
                <Space>
                    {/* Accés per a professors */}
                    {(user.tipus === "0" || user.tipus === "1") && (
                        <Tooltip title={onlyExams ? "Accedir a l'examen" : "Accedir a l'activitat"}>
                            <Button
                                shape="circle"
                                style={{ backgroundColor: BbddColors.colors.color6, border: 'none', color: '#fff' }}
                                icon={<BiArrowFromLeft />}
                                onClick={() => navigate(`/database/activitats/${ele.id}`)}
                            />
                        </Tooltip>
                    )}

                    {/* Accés per a alumnes en activitats normals (no examen) */}
                    {(user.tipus === "2" && ele.is_examen !== 1 && ele.estat === 3) && (
                        <Tooltip title="Accedir a l'activitat">
                            <Button
                                shape="circle"
                                style={{ backgroundColor: BbddColors.colors.color6, border: 'none', color: '#fff' }}
                                icon={<BiArrowFromLeft />}
                                onClick={() => navigate(`/database/activitats/${ele.id}`)}
                            />
                        </Tooltip>
                    )}

                    {/* Accés per a alumnes en exàmens: botó únic 'Iniciar exàmen' (un sol ús) */}
                    {(user.tipus === "2" && ele.is_examen === 1 && !ele.started_exam && ele.estat === 3) && (
                        <Tooltip title="Començar Examen (Un sol ús)">
                            <Button
                                type="primary"
                                style={{ backgroundColor: '#fa541c', borderColor: '#fa541c', color: '#fff', fontWeight: 600 }}
                                icon={<FaLaptopCode />}
                                loading={startingExamId === ele.id}
                                onClick={() => handleStartExam(ele)}
                            >
                                Iniciar exàmen
                            </Button>
                        </Tooltip>
                    )}

                    {isTeacher && (
                        <Tooltip title="Seguiment d'alumnes">
                            <Button
                                shape="circle"
                                style={{ backgroundColor: '#1890ff', border: 'none', color: '#fff' }}
                                icon={<FaUsers />}
                                onClick={() => {
                                    setSelected(ele);
                                    setModalAlumnes(true);
                                }}
                            />
                        </Tooltip>
                    )}

                    {/* Revisió per a alumnes: exàmens TANCAT (2) o en REVISIÓ (4) -> pot entrar a
                        veure els exercicis i la nota (en mode només lectura). */}
                    {(user.tipus === "2" && ele.is_examen === 1 && (ele.estat === 2 || ele.estat === 4)) && (
                        <Tooltip title="Revisar l'examen i veure la nota">
                            <Button
                                shape="circle"
                                style={{ backgroundColor: BbddColors.colors.color3, border: 'none', color: '#fff' }}
                                icon={<FaEye />}
                                onClick={() => navigate(`/database/activitats/${ele.id}`)}
                            />
                        </Tooltip>
                    )}

                    {isTeacher && (
                        <Tooltip title="Exercicis de l'activitat">
                            <Button
                                shape="circle"
                                style={{ backgroundColor: BbddColors.colors.color3, border: 'none', color: '#fff' }}
                                icon={<FaListCheck />}
                                onClick={() => showExercicis(ele)}
                            />
                        </Tooltip>
                    )}

                    {isTeacher && (
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'exercicis',
                                        label: 'Exercicis',
                                        icon: <FaListCheck style={{ color: BbddColors.colors.color3 }} />,
                                        onClick: () => showExercicis(ele)
                                    },
                                    {
                                        key: 'seguiment',
                                        label: 'Seguiment Alumnes',
                                        icon: <FaUsers style={{ color: '#1890ff' }} />,
                                        onClick: () => {
                                            setSelected(ele);
                                            setModalAlumnes(true);
                                        }
                                    },
                                    {
                                        key: 'configurar',
                                        label: 'Configurar',
                                        icon: <VscSettingsGear style={{ color: BbddColors.colors.color4 }} />,
                                        onClick: () => showSettings(ele)
                                    },
                                    {
                                        key: 'descarregarPdf',
                                        label: 'Descarregar PDF',
                                        icon: <FaFilePdf style={{ color: BbddColors.colors.color3 }} />,
                                        onClick: () => getPdf(ele)
                                    },
                                    {
                                        key: 'descarregarSol',
                                        label: 'Descarregar Solució',
                                        icon: <FaRegFilePdf style={{ color: BbddColors.colors.color2 }} />,
                                        onClick: () => getPdfSol(ele)
                                    }
                                ]
                            }}
                            trigger={['click']}
                        >
                            <Button shape="circle" icon={<MoreOutlined />} />
                        </Dropdown>
                    )}
                </Space>
            )
        }
    ];

    const tableProps: TableProps<any> = {
        loading,
        pagination: isTeacher ? false : { pageSize: 8 },
        showHeader: true,
    };

    function setPnt(value: number) {
        setPantalla(value);
    }

    return (
        <>
            <AppSider setPantalla={setPnt} currentVal={onlyExams ? 3 : 2} />
            <Content style={{ background: '#f8f9fb', padding: '16px 24px', minHeight: 'calc(100vh - 120px)' }}>
                {/* Header Filter Bar */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                    flexWrap: 'wrap',
                    gap: 12
                }}>
                    <div>
                        <Typography.Title level={3} style={{ margin: 0, fontWeight: 700 }}>
                            {onlyExams ? 'Exàmens de Base de Dades' : 'Activitats de Base de Dades'}
                        </Typography.Title>
                        <Text type="secondary">
                            {onlyExams
                                ? 'Visualització, configuració i seguiment dels exàmens pràctics'
                                : 'Activitats i pràctiques d\'aprenentatge del mòdul de base de dades'}
                        </Text>
                    </div>

                    <Space wrap>
                        <Input.Search
                            placeholder="Cercar..."
                            allowClear
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 220 }}
                        />

                        {availableAulas.length > 0 && (
                            <Select
                                value={selectedAula}
                                onChange={setSelectedAula}
                                style={{ width: 160 }}
                            >
                                <Select.Option value="all">Totes les aules</Select.Option>
                                {availableAulas.map((aula) => (
                                    <Select.Option key={aula.id} value={aula.id}>
                                        {aula.nom}
                                    </Select.Option>
                                ))}
                            </Select>
                        )}

                        {isTeacher && (
                            <Button
                                type="primary"
                                icon={<BiPlus />}
                                onClick={() => {
                                    setSelected({ id: 0, estat: 0, is_examen: onlyExams ? 1 : 0 });
                                    setModal(true);
                                }}
                                style={{
                                    backgroundColor: onlyExams ? '#fa8c16' : AvaluacioColors.colors.color6,
                                    borderColor: onlyExams ? '#fa8c16' : AvaluacioColors.colors.color6,
                                    fontWeight: 600
                                }}
                            >
                                {onlyExams ? 'Nou Examen' : 'Nova Activitat'}
                            </Button>
                        )}
                    </Space>
                </div>

                <Card
                    bordered={false}
                    style={{
                        boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
                        background: '#fff',
                        borderRadius: '16px',
                        overflow: 'hidden'
                    }}
                    bodyStyle={{ padding: 0 }}
                >
                    {isTeacher ? (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                            <SortableContext items={filteredDades.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                                <Table
                                    {...tableProps}
                                    columns={columns}
                                    dataSource={filteredDades}
                                    components={{
                                        body: {
                                            row: TableDragRow,
                                        },
                                    }}
                                    size="small"
                                    rowKey="id"
                                    bordered={false}
                                />
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <Table
                            {...tableProps}
                            columns={columns}
                            dataSource={filteredDades}
                            size="small"
                            rowKey="id"
                            bordered={false}
                        />
                    )}
                </Card>
            </Content>

            {!loading && (
                <FloatButton.Group
                    trigger="click"
                    type="primary"
                    style={{ insetInlineStart: 97 }}
                    icon={<IoOptions />}
                >
                    {isTeacher && (
                        <>
                            <FloatButton
                                shape="circle"
                                tooltip={onlyExams ? "Crear Examen" : "Crear Activitat"}
                                onClick={() => {
                                    setSelected({ id: 0, estat: 0, is_examen: onlyExams ? 1 : 0 });
                                    setModal(true);
                                }}
                                style={{ backgroundColor: onlyExams ? '#fa8c16' : AvaluacioColors.colors.color6 }}
                                icon={<BiPlus />}
                            />
                            <FloatButton
                                shape="circle"
                                tooltip="Copiar Activitat"
                                onClick={() => setCopyModal(true)}
                                style={{ backgroundColor: BbddColors.colors.color5 }}
                                icon={<BiCopy />}
                            />
                        </>
                    )}
                    <FloatButton
                        shape="circle"
                        type="default"
                        tooltip="Actualitzar"
                        onClick={checkDades}
                        style={{ backgroundColor: BbddColors.colors.color6 }}
                        icon={<BiRefresh />}
                    />
                </FloatButton.Group>
            )}

            {modal && (
                <ConfigActivitatModal
                    element={selected}
                    visible={modal}
                    onClose={onCloseModal}
                />
            )}

            {copyModal && (
                <CopiarActivitatModal
                    visible={copyModal}
                    onClose={(updated) => {
                        setCopyModal(false);
                        if (updated) checkDades();
                    }}
                />
            )}

            {modalExercicis && selected && (
                <ExercicisModal
                    nom={selected.nom || `Activitat ${selected.id}`}
                    id_activitat={selected.id + ""}
                    visible={modalExercicis}
                    onClose={onCloseModal}
                />
            )}

            {modalAlumnes && selected && (
                <ActivitatAlumnesBbddModal
                    visible={modalAlumnes}
                    activitat={selected}
                    onClose={() => {
                        setModalAlumnes(false);
                        checkDades();
                    }}
                />
            )}
        </>
    );
};

export default Activitats;
